import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowRight, PlayCircle } from "lucide-react";
import { Button } from "./ui/button";
import { HERO_SLIDES } from "../mock";

export default function Hero() {
  const [idx, setIdx] = useState(0);
  const total = HERO_SLIDES.length;

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % total), 5500);
    return () => clearInterval(id);
  }, [total]);

  const slide = HERO_SLIDES[idx];

  return (
    <section className="relative w-full overflow-hidden bg-slate-900" style={{ height: "calc(100vh - 80px)", minHeight: 600 }}>
      {HERO_SLIDES.map((s, i) => (
        <div
          key={s.id}
          className="absolute inset-0 transition-opacity duration-1000"
          style={{ opacity: i === idx ? 1 : 0, zIndex: i === idx ? 1 : 0 }}
        >
          <img
            src={s.image}
            alt={s.title}
            className="absolute inset-0 w-full h-full object-cover kenburns"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/55 to-slate-950/30" />
        </div>
      ))}

      {/* content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 h-full flex items-center">
        <div className="max-w-2xl text-white">
          <div key={slide.id} className="fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-700/90 text-white text-xs font-semibold tracking-widest uppercase rounded">
              <span className="w-1.5 h-1.5 bg-white rounded-full" />
              {slide.eyebrow}
            </div>
            <h1 className="display mt-6 text-5xl md:text-7xl font-extrabold leading-[1.02] uppercase">
              {slide.title}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-200 max-w-xl leading-relaxed">
              {slide.subtitle}
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#contact">
                <Button size="lg" className="bg-yellow-700 hover:bg-yellow-800 text-white font-semibold px-7 h-12 btn-shine">
                  Get a Free Quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="#services">
                <Button size="lg" variant="outline" className="border-white/40 text-white bg-white/5 hover:bg-white hover:text-slate-900 font-semibold px-7 h-12">
                  <PlayCircle className="w-5 h-5 mr-2" /> Explore Services
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* arrows */}
      <button
        onClick={() => setIdx((idx - 1 + total) % total)}
        className="absolute z-20 left-4 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/10 hover:bg-yellow-700 text-white border border-white/20 backdrop-blur transition-colors"
        aria-label="Previous"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={() => setIdx((idx + 1) % total)}
        className="absolute z-20 right-4 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center rounded-full bg-white/10 hover:bg-yellow-700 text-white border border-white/20 backdrop-blur transition-colors"
        aria-label="Next"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* dots */}
      <div className="absolute z-20 bottom-7 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-10 bg-yellow-600" : "w-3 bg-white/40 hover:bg-white/70"}`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

      {/* slide counter */}
      <div className="absolute z-20 right-6 bottom-6 text-white/80 text-sm font-mono">
        <span className="text-yellow-600 text-lg font-bold">{String(idx + 1).padStart(2, "0")}</span> / {String(total).padStart(2, "0")}
      </div>
    </section>
  );
}
